import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL = 2000;

export default function OrderConfirmation() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const nav = useNavigate();
  const [state, setState] = useState("polling"); // polling | success | failed | timeout
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    let active = true;

    const poll = async () => {
      if (!active) return;
      if (attempts >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!clearedRef.current) { clear(); clearedRef.current = true; }
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        setTimeout(poll, POLL_INTERVAL);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]); // eslint-disable-line

  return (
    <div className="max-w-lg mx-auto px-5 py-24 text-center" data-testid="order-confirmation-page">
      {state === "polling" && (
        <div data-testid="payment-polling">
          <Loader2 size={56} className="mx-auto animate-spin" />
          <h1 className="font-anton text-4xl uppercase tracking-tighter mt-6">Confirming Payment</h1>
          <p className="font-mono text-sm text-muted-foreground mt-3">Hang tight, verifying with Stripe<span className="animate-ping">_</span></p>
        </div>
      )}
      {state === "success" && (
        <div data-testid="payment-success">
          <CheckCircle2 size={56} className="mx-auto text-primary" />
          <h1 className="font-anton text-5xl uppercase tracking-tighter mt-6">Order Locked In</h1>
          <p className="font-mono text-sm text-muted-foreground mt-3">Payment received. We'll print &amp; ship your gear. Check your account for order details.</p>
          <div className="flex gap-3 justify-center mt-8">
            <button onClick={() => nav("/account")} data-testid="view-orders-btn" className="bg-primary text-primary-foreground font-outfit font-bold uppercase tracking-widest text-sm px-6 py-3 border-2 border-border hover:bg-secondary hover:border-secondary hover:text-white transition-colors">My Orders</button>
            <Link to="/shop" className="font-outfit font-bold uppercase tracking-widest text-sm px-6 py-3 border-2 border-border hover:bg-primary hover:text-black transition-colors">Keep Shopping</Link>
          </div>
        </div>
      )}
      {(state === "failed" || state === "timeout") && (
        <div data-testid="payment-failed">
          <XCircle size={56} className="mx-auto text-primary" />
          <h1 className="font-anton text-4xl uppercase tracking-tighter mt-6">{state === "timeout" ? "Still Processing" : "Payment Not Completed"}</h1>
          <p className="font-mono text-sm text-muted-foreground mt-3">
            {state === "timeout" ? "Taking longer than expected. Check My Orders shortly — if charged, your order will appear." : "Your payment wasn't completed. Your cart is still saved."}
          </p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/cart" className="bg-primary text-primary-foreground font-outfit font-bold uppercase tracking-widest text-sm px-6 py-3 border-2 border-border hover:bg-secondary hover:border-secondary hover:text-white transition-colors">Back To Cart</Link>
            <Link to="/account" className="font-outfit font-bold uppercase tracking-widest text-sm px-6 py-3 border-2 border-border hover:bg-primary hover:text-black transition-colors">My Orders</Link>
          </div>
        </div>
      )}
    </div>
  );
}
